import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  RuleAction,
  Segment,
  TelegramActionType,
  TelegramHandlerInfo,
  TelegramListeningChat,
  VariableType,
} from '@wave/shared';
import { CCY_CODES, TELEGRAM_SYSTEM_PLACEHOLDERS, VARIABLE_TYPE_LABELS } from '@wave/shared';
import { useCurrencies } from '../../api/hooks';
import { Combobox } from '../Combobox';
import {
  btnGhostSm,
  cardL2,
  fieldLabelCls,
  inputCls,
  selectCls,
  textareaCls,
} from '../../lib/styles';

/**
 * Живые значения переменных относительно размеченного примера (sample).
 * Вычисляются клиентски в TelegramRuleEditorModal (new RegExp + матчинг
 * sample, без запросов) и прокидываются сюда для индикативного отображения
 * в чипах/селекторах: оператор видит, какие КОНКРЕТНО данные сейчас в каждой
 * переменной. Вставка по клику не меняется — вставляется только {имя}.
 */
export interface TemplateLiveValues {
  /** Named groups первого матча sample (undefined — группа не захватила). */
  groups: Record<string, string | undefined>;
  /** Named groups всех матчей (длина > 1 при флаге g — мультиматчинг). */
  allGroups: Array<Record<string, string | undefined>>;
  /** Значения системных плейсхолдеров, известные без запросов (_message_text, _chat_id, …). */
  system: Record<string, string>;
}

/**
 * Конструктор цепочки действий. Каждое action — отдельная карточка с per-type
 * формой (без сырого JSON). Цепочка sequentially выполняется при apply event.
 *
 * Особенности:
 *  - Drag-n-drop отсутствует (MVP) — порядок через ↑↓ кнопки.
 *  - Каждая карточка collapsible (свернуть/раскрыть для компактности).
 *  - Per-type form знает о placeholders (имена named groups из segments)
 *    и предлагает их в селекторах/pill-кнопках для вставки в шаблоны.
 *  - При смене типа — config сбрасывается, чтобы не было мусора от прежнего.
 */
export function ActionChainBuilder({
  actions,
  onChange,
  handlers,
  segments,
  chats,
  live = null,
}: {
  actions: RuleAction[];
  onChange: (a: RuleAction[]) => void;
  handlers: TelegramHandlerInfo[];
  segments: Segment[];
  chats: TelegramListeningChat[];
  /** Живые значения переменных из sample — индикативно показываются в чипах. */
  live?: TemplateLiveValues | null;
}) {
  const placeholders = useMemo(
    () =>
      segments
        .filter((s) => s.kind === 'placeholder')
        .map((s) => ({
          name: (s as { name: string }).name,
          type: (s as { type: VariableType }).type,
        })),
    [segments],
  );

  function addAction() {
    onChange([...actions, { type: 'log-only', config: {} }]);
  }

  function updateAt(i: number, patch: Partial<RuleAction>) {
    onChange(actions.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  }

  function removeAt(i: number) {
    onChange(actions.filter((_, idx) => idx !== i));
  }

  function moveAt(i: number, dir: -1 | 1) {
    const target = i + dir;
    if (target < 0 || target >= actions.length) return;
    const next = [...actions];
    [next[i], next[target]] = [next[target]!, next[i]!];
    onChange(next);
  }

  return (
    <div className="space-y-2">
      {actions.length === 0 && (
        <div className="rounded-md border border-dashed border-border bg-bg px-3 py-3 text-xs text-text-subtle">
          Добавь хотя бы одно действие — что делать когда правило сработает.
        </div>
      )}
      {actions.map((a, i) => (
        <ActionCard
          key={i}
          index={i}
          total={actions.length}
          action={a}
          handlers={handlers}
          placeholders={placeholders}
          chats={chats}
          live={live}
          onUpdate={(patch) => updateAt(i, patch)}
          onRemove={() => removeAt(i)}
          onMoveUp={() => moveAt(i, -1)}
          onMoveDown={() => moveAt(i, 1)}
        />
      ))}
      <button
        type="button"
        onClick={addAction}
        className="w-full rounded-md border border-dashed border-border bg-bg2 py-2 text-xs font-medium text-text2 hover:border-accent hover:bg-[var(--accent-bg)] hover:text-accent"
      >
        + Добавить действие
      </button>
    </div>
  );
}

/** Одна карточка действия с заголовком и per-type формой. */
function ActionCard({
  index,
  total,
  action,
  handlers,
  placeholders,
  chats,
  live,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  index: number;
  total: number;
  action: RuleAction;
  handlers: TelegramHandlerInfo[];
  placeholders: { name: string; type: VariableType }[];
  chats: TelegramListeningChat[];
  live: TemplateLiveValues | null;
  onUpdate: (patch: Partial<RuleAction>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const handler = handlers.find((h) => h.type === action.type);

  function changeType(newType: TelegramActionType) {
    // При смене типа сбрасываем config — defaults применит handler при validate.
    onUpdate({ type: newType, config: {} });
  }

  return (
    <div className={cardL2}>
      {/* Header */}
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--accent-bg)] text-[11px] font-bold text-accent">
          {index + 1}
        </span>
        <select
          value={action.type}
          onChange={(e) => changeType(e.target.value as TelegramActionType)}
          className={`${selectCls} h-7 flex-1 text-xs`}
        >
          {handlers.map((h) => (
            <option key={h.type} value={h.type}>
              {h.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={index === 0}
          onClick={onMoveUp}
          className={btnGhostSm}
          title="Поднять"
        >
          ↑
        </button>
        <button
          type="button"
          disabled={index === total - 1}
          onClick={onMoveDown}
          className={btnGhostSm}
          title="Опустить"
        >
          ↓
        </button>
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className={btnGhostSm}
          title={collapsed ? 'Развернуть' : 'Свернуть'}
        >
          {collapsed ? '▸' : '▾'}
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="rounded-md px-2 py-1 text-xs text-danger hover:bg-[var(--red-bg)]"
          title="Удалить действие"
        >
          ✕
        </button>
      </div>

      {handler?.description && !collapsed && (
        <div className="mt-1 text-[10px] text-text-subtle">{handler.description}</div>
      )}

      {/* Per-type form */}
      {!collapsed && (
        <div className="mt-3">
          <PerTypeForm
            type={action.type}
            config={action.config}
            placeholders={placeholders}
            chats={chats}
            live={live}
            onChange={(config) => onUpdate({ config })}
          />
        </div>
      )}
    </div>
  );
}

/** Switch per-type forms. */
function PerTypeForm({
  type,
  config,
  placeholders,
  chats,
  live,
  onChange,
}: {
  type: TelegramActionType;
  config: Record<string, unknown>;
  placeholders: { name: string; type: VariableType }[];
  chats: TelegramListeningChat[];
  live: TemplateLiveValues | null;
  onChange: (next: Record<string, unknown>) => void;
}) {
  switch (type) {
    case 'log-only':
      return <FormLogOnly />;
    case 'update-rate':
      return (
        <FormUpdateRate config={config} placeholders={placeholders} live={live} onChange={onChange} />
      );
    case 'confirm-provodka':
      return (
        <FormConfirmProvodka
          config={config}
          placeholders={placeholders}
          live={live}
          onChange={onChange}
        />
      );
    case 'reject-order':
      return (
        <FormRejectOrder config={config} placeholders={placeholders} live={live} onChange={onChange} />
      );
    case 'edit-provodka':
      return (
        <FormEditProvodka config={config} placeholders={placeholders} live={live} onChange={onChange} />
      );
    case 'send-message':
      return (
        <FormSendMessage
          config={config}
          placeholders={placeholders}
          chats={chats}
          live={live}
          onChange={onChange}
        />
      );
    case 'custom-webhook':
      return (
        <FormCustomWebhook
          config={config}
          placeholders={placeholders}
          live={live}
          onChange={onChange}
        />
      );
    case 'create-order':
      return (
        <FormCreateOrder config={config} placeholders={placeholders} live={live} onChange={onChange} />
      );
    case 'update-chat-balance':
      return (
        <FormUpdateChatBalance
          config={config}
          placeholders={placeholders}
          chats={chats}
          live={live}
          onChange={onChange}
        />
      );
  }
}

// ─── helpers ────────────────────────────────────────────────────────────────

/** Обрезка живого значения для компактного отображения в чипе/option. */
function truncateValue(v: string, max = 20): string {
  return v.length > max ? `${v.slice(0, max)}…` : v;
}

/**
 * Title чипа capture-группы: тип переменной; если группа в текущем sample
 * ничего не захватила — явная пометка; при мультиматче (флаг g) — значения
 * первых матчей, чтобы наглядно объяснить, откуда берётся «первое» значение.
 */
function captureChipTitle(
  name: string,
  type: VariableType,
  live: TemplateLiveValues | null,
): string {
  const base = VARIABLE_TYPE_LABELS[type];
  if (!live) return base;
  const value = live.groups[name];
  if (value === undefined) return `${base} — нет данных в примере`;
  if (live.allGroups.length > 1) {
    const shown = live.allGroups
      .slice(0, 4)
      .map((g, i) => `#${i + 1}: ${g[name] ?? '—'}`)
      .join(', ');
    const more = live.allGroups.length > 4 ? '…' : '';
    return `${base} — совпадений ${live.allGroups.length}: ${shown}${more}`;
  }
  return base;
}

/**
 * Чип capture-группы: вставляемый токен `{имя}` + индикативное live-значение
 * из первого матча sample (моноширинно, приглушённо, truncate). Клик вставляет
 * только `{имя}` — значение чисто информационное.
 */
function CaptureChip({
  p,
  live,
  onClick,
}: {
  p: { name: string; type: VariableType };
  live: TemplateLiveValues | null;
  onClick: () => void;
}) {
  const value = live?.groups[p.name];
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded border border-border bg-bg px-1.5 py-0.5 font-mono text-[10px] text-text2 hover:border-accent hover:text-accent"
      title={captureChipTitle(p.name, p.type, live ?? null)}
    >
      {`{${p.name}}`}
      {value !== undefined && (
        <span className="text-text-subtle">· {truncateValue(value)}</span>
      )}
    </button>
  );
}

function PlaceholderPills({
  placeholders,
  onInsert,
  live = null,
}: {
  placeholders: { name: string; type: VariableType }[];
  onInsert: (name: string) => void;
  /** Живые значения из sample — индикативно показываются в чипах. */
  live?: TemplateLiveValues | null;
}) {
  if (placeholders.length === 0) {
    return (
      <div className="mt-1 text-[10px] text-text-subtle">
        Сначала размечь переменные в шаблоне сверху — они появятся здесь как вставляемые pill-токены.
      </div>
    );
  }
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1 text-[10px]">
      <span className="text-text-subtle">Вставить:</span>
      {placeholders.map((p) => (
        <CaptureChip key={p.name} p={p} live={live} onClick={() => onInsert(p.name)} />
      ))}
    </div>
  );
}

/**
 * Панель чипов для шаблонных полей send-message / custom-webhook:
 * сначала capture-группы правила ({имя}), затем — визуально отделённые —
 * системные плейсхолдеры ({_chat_title}, {_message_link}, …) с подсказками.
 * Клик по чипу вставляет токен в позицию курсора last-focused textarea.
 * При наличии live-значений из sample чипы показывают их индикативно:
 * `{ccy} · USDT` — значение первого матча (мультиматч — в title).
 */
function TemplatePlaceholderChips({
  placeholders,
  onInsert,
  live = null,
}: {
  placeholders: { name: string; type: VariableType }[];
  onInsert: (token: string) => void;
  /** Живые значения из sample — индикативно показываются в чипах. */
  live?: TemplateLiveValues | null;
}) {
  return (
    <div className="mt-1 space-y-1">
      <div className="flex flex-wrap items-center gap-1 text-[10px]">
        <span className="text-text-subtle">Из сообщения:</span>
        {placeholders.length === 0 ? (
          <span className="text-text-subtle">
            размечь переменные в шаблоне сверху — они появятся здесь
          </span>
        ) : (
          placeholders.map((p) => (
            <CaptureChip key={p.name} p={p} live={live} onClick={() => onInsert(`{${p.name}}`)} />
          ))
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1 border-t border-border-subtle pt-1 text-[10px]">
        <span className="text-text-subtle">Системные:</span>
        {TELEGRAM_SYSTEM_PLACEHOLDERS.map((p) => {
          // Живые значения только там, где они известны без запросов
          // (_message_text / _chat_id / _chat_title); остальные — как раньше.
          const value = live?.system[p.key];
          const baseTitle =
            p.key === '_message_link' && !/супергрупп/i.test(p.description)
              ? `${p.label} — ${p.description} (только супергруппы/каналы)`
              : `${p.label} — ${p.description}`;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => onInsert(`{${p.key}}`)}
              className="inline-flex items-center gap-1 rounded border border-dashed border-border bg-bg2 px-1.5 py-0.5 font-mono text-[10px] text-text-subtle hover:border-accent hover:text-accent"
              title={value ? `${baseTitle}\nСейчас: ${truncateValue(value, 120)}` : baseTitle}
            >
              {`{${p.key}}`}
              {value !== undefined && value !== '' && (
                <span className="text-text-subtle">· {truncateValue(value)}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Универсальный селектор named group из placeholders. */
function GroupSelector({
  value,
  onChange,
  placeholders,
  filterTypes,
  allowEmpty,
  liveGroups,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholders: { name: string; type: VariableType }[];
  filterTypes?: VariableType[];
  allowEmpty?: boolean;
  /** Live-значения групп из sample — индикативно в тексте option ("ccy (валюта) — USDT"). */
  liveGroups?: Record<string, string | undefined> | null;
}) {
  const filtered = filterTypes
    ? placeholders.filter((p) => filterTypes.includes(p.type))
    : placeholders;
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={`${selectCls} h-8 text-xs`}>
      {allowEmpty && <option value="">— нет —</option>}
      {!allowEmpty && !filtered.find((p) => p.name === value) && (
        <option value="">— выбери переменную —</option>
      )}
      {filtered.map((p) => {
        const liveValue = liveGroups?.[p.name];
        return (
          <option key={p.name} value={p.name}>
            {`${p.name} (${VARIABLE_TYPE_LABELS[p.type]})${
              liveValue !== undefined ? ` — ${truncateValue(liveValue)}` : ''
            }`}
          </option>
        );
      })}
    </select>
  );
}

/** Textarea с поддержкой pill-insertion (вставка {placeholder} в место курсора). */
function TemplateTextarea({
  value,
  onChange,
  placeholder,
  minRows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  minRows?: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(minRows * 18 + 16, Math.min(300, el.scrollHeight))}px`;
  }, [value, minRows]);

  // Регистрируем globally last-focused textarea — PlaceholderPills вставляет
  // {placeholder} в неё через `insertIntoLastTextarea` ниже.
  function markFocused(el: HTMLTextAreaElement) {
    (window as unknown as { __waveLastTextarea?: HTMLTextAreaElement }).__waveLastTextarea = el;
  }
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={(e) => markFocused(e.currentTarget)}
      onClick={(e) => markFocused(e.currentTarget)}
      placeholder={placeholder}
      rows={minRows}
      className={`${textareaCls} w-full overflow-hidden font-mono text-xs leading-snug`}
    />
  );
}

/**
 * Глобальный helper: вставить {name} в last-focused textarea.
 * Используется для PlaceholderPills onInsert handler.
 */
function insertIntoLastTextarea(text: string, fallback: (v: string) => void, currentValue: string) {
  const ta = (window as unknown as { __waveLastTextarea?: HTMLTextAreaElement }).__waveLastTextarea;
  if (!ta) {
    fallback(currentValue + text);
    return;
  }
  const start = ta.selectionStart;
  const end = ta.selectionEnd;
  const next = ta.value.slice(0, start) + text + ta.value.slice(end);
  // Trigger React onChange (нужно сэмулировать).
  const setter = Object.getOwnPropertyDescriptor(
    window.HTMLTextAreaElement.prototype,
    'value',
  )?.set;
  if (setter) {
    setter.call(ta, next);
    ta.dispatchEvent(new Event('input', { bubbles: true }));
  }
  requestAnimationFrame(() => {
    ta.focus();
    ta.setSelectionRange(start + text.length, start + text.length);
  });
}

// ════════════════════════════════════════════════════════════════════════════
// Per-type forms
// ════════════════════════════════════════════════════════════════════════════

function FormLogOnly() {
  return (
    <div className="text-xs text-text-subtle">
      Никакого действия не выполняется — только запись совпадения в журнал событий. Полезно для
      отладки правил.
    </div>
  );
}

function FormUpdateRate({
  config,
  placeholders,
  live,
  onChange,
}: {
  config: Record<string, unknown>;
  placeholders: { name: string; type: VariableType }[];
  live: TemplateLiveValues | null;
  onChange: (next: Record<string, unknown>) => void;
}) {
  // Валюты из справочника (вкл. добавленные через UI); fallback — встроенные.
  const { data: currenciesList } = useCurrencies();
  const ccyCodes = currenciesList?.length ? currenciesList.map((c) => c.code) : [...CCY_CODES];

  const ccyGroup = String(config.ccyGroup ?? 'ccy');
  const rateGroup = String(config.rateGroup ?? 'rate');
  const defaultCcy = (config.defaultCcy as string | undefined) ?? '';
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div>
        <label className={fieldLabelCls}>Переменная с валютой</label>
        <GroupSelector
          value={ccyGroup}
          onChange={(v) => onChange({ ...config, ccyGroup: v })}
          placeholders={placeholders}
          filterTypes={['ccy', 'anyText']}
          liveGroups={live?.groups}
        />
        <div className="mt-1.5 text-[10px] text-text-subtle">
          Откуда взять код валюты (RUB / USDT и т.п.).
        </div>
      </div>
      <div>
        <label className={fieldLabelCls}>Переменная с курсом</label>
        <GroupSelector
          value={rateGroup}
          onChange={(v) => onChange({ ...config, rateGroup: v })}
          placeholders={placeholders}
          filterTypes={['amount', 'anyNumber']}
          liveGroups={live?.groups}
        />
        <div className="mt-1.5 text-[10px] text-text-subtle">
          Откуда взять числовой курс — обновится в справочнике.
        </div>
      </div>
      <div className="md:col-span-2">
        <label className={fieldLabelCls}>
          Валюта по умолчанию (если переменной нет в сообщении)
        </label>
        <select
          value={defaultCcy}
          onChange={(e) =>
            onChange({
              ...config,
              defaultCcy: e.target.value || undefined,
            })
          }
          className={`${selectCls} h-8 max-w-xs text-xs`}
        >
          <option value="">— не задана —</option>
          {ccyCodes.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <div className="mt-1.5 text-[10px] text-text-subtle">
          Поддерживаются все валюты системы (RUB, USD, EUR, CNY, USDT, AED). Курс хранится как
          коэффициент к USDT — пары между валютами вычисляются автоматически.
        </div>
      </div>
    </div>
  );
}

function FormConfirmProvodka({
  config,
  placeholders,
  live,
  onChange,
}: {
  config: Record<string, unknown>;
  placeholders: { name: string; type: VariableType }[];
  live: TemplateLiveValues | null;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const orderIdGroup = String(config.orderIdGroup ?? 'orderId');
  const idxGroup = (config.idxGroup as string | undefined) ?? '';
  const defaultIdx = Number(config.defaultIdx ?? 0);
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      <div>
        <label className={fieldLabelCls}>Переменная с ID заявки</label>
        <GroupSelector
          value={orderIdGroup}
          onChange={(v) => onChange({ ...config, orderIdGroup: v })}
          placeholders={placeholders}
          filterTypes={['orderId', 'anyText']}
          liveGroups={live?.groups}
        />
      </div>
      <div>
        <label className={fieldLabelCls}>Переменная с idx (0–3)</label>
        <GroupSelector
          value={idxGroup}
          onChange={(v) => onChange({ ...config, idxGroup: v || undefined })}
          placeholders={placeholders}
          filterTypes={['idx', 'anyNumber']}
          allowEmpty
          liveGroups={live?.groups}
        />
        <div className="mt-1.5 text-[10px] text-text-subtle">
          Опционально — иначе берётся idx из «по умолчанию».
        </div>
      </div>
      <div>
        <label className={fieldLabelCls}>Idx по умолчанию</label>
        <select
          value={defaultIdx}
          onChange={(e) => onChange({ ...config, defaultIdx: Number(e.target.value) })}
          className={`${selectCls} h-8 w-24 text-xs`}
        >
          {[0, 1, 2, 3].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <div className="mt-1.5 text-[10px] text-text-subtle">
          0=клиент→нам, 1=мы→клиенту, 2=мы→исп., 3=исп.→нам
        </div>
      </div>
    </div>
  );
}

function FormRejectOrder({
  config,
  placeholders,
  live,
  onChange,
}: {
  config: Record<string, unknown>;
  placeholders: { name: string; type: VariableType }[];
  live: TemplateLiveValues | null;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const orderIdGroup = String(config.orderIdGroup ?? 'orderId');
  const reasonTemplate = String(config.reasonTemplate ?? '');
  return (
    <div className="space-y-3">
      <div>
        <label className={fieldLabelCls}>Переменная с ID заявки</label>
        <GroupSelector
          value={orderIdGroup}
          onChange={(v) => onChange({ ...config, orderIdGroup: v })}
          placeholders={placeholders}
          filterTypes={['orderId', 'anyText']}
          liveGroups={live?.groups}
        />
      </div>
      <div>
        <label className={fieldLabelCls}>Шаблон причины отклонения</label>
        <TemplateTextarea
          value={reasonTemplate}
          onChange={(v) => onChange({ ...config, reasonTemplate: v })}
          placeholder="Например: Платёж отозван банком — {amount} {ccy}"
          minRows={2}
        />
        <PlaceholderPills
          placeholders={placeholders}
          live={live}
          onInsert={(name) =>
            insertIntoLastTextarea(`{${name}}`, (v) => onChange({ ...config, reasonTemplate: v }), reasonTemplate)
          }
        />
      </div>
    </div>
  );
}

function FormEditProvodka({
  config,
  placeholders,
  live,
  onChange,
}: {
  config: Record<string, unknown>;
  placeholders: { name: string; type: VariableType }[];
  live: TemplateLiveValues | null;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const orderIdGroup = String(config.orderIdGroup ?? 'orderId');
  const idxGroup = (config.idxGroup as string | undefined) ?? '';
  const defaultIdx = Number(config.defaultIdx ?? 0);
  const amountGroup = (config.amountGroup as string | undefined) ?? '';
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div>
        <label className={fieldLabelCls}>Переменная с ID заявки</label>
        <GroupSelector
          value={orderIdGroup}
          onChange={(v) => onChange({ ...config, orderIdGroup: v })}
          placeholders={placeholders}
          filterTypes={['orderId', 'anyText']}
          liveGroups={live?.groups}
        />
      </div>
      <div>
        <label className={fieldLabelCls}>Переменная с новой суммой</label>
        <GroupSelector
          value={amountGroup}
          onChange={(v) => onChange({ ...config, amountGroup: v || undefined })}
          placeholders={placeholders}
          filterTypes={['amount', 'anyNumber']}
          allowEmpty
          liveGroups={live?.groups}
        />
      </div>
      <div>
        <label className={fieldLabelCls}>Переменная с idx</label>
        <GroupSelector
          value={idxGroup}
          onChange={(v) => onChange({ ...config, idxGroup: v || undefined })}
          placeholders={placeholders}
          filterTypes={['idx', 'anyNumber']}
          allowEmpty
          liveGroups={live?.groups}
        />
      </div>
      <div>
        <label className={fieldLabelCls}>Idx по умолчанию</label>
        <select
          value={defaultIdx}
          onChange={(e) => onChange({ ...config, defaultIdx: Number(e.target.value) })}
          className={`${selectCls} h-8 w-24 text-xs`}
        >
          {[0, 1, 2, 3].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function FormSendMessage({
  config,
  placeholders,
  chats,
  live,
  onChange,
}: {
  config: Record<string, unknown>;
  placeholders: { name: string; type: VariableType }[];
  chats: TelegramListeningChat[];
  live: TemplateLiveValues | null;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const targetChatIds = (config.targetChatIds as string[] | undefined) ?? [];
  const template = String(config.template ?? '');
  const parseMode = String(config.parseMode ?? 'HTML') as 'HTML' | 'MarkdownV2' | 'plain';
  const disableNotification = Boolean(config.disableNotification);
  const replyToOriginal = Boolean(config.replyToOriginal);
  const messageThreadId = (config.messageThreadId as number | undefined) ?? '';
  return (
    <div className="space-y-3">
      <div>
        <label className={fieldLabelCls}>Куда отправить (один или несколько чатов)</label>
        <div className="max-h-32 space-y-1 overflow-y-auto rounded-md border border-border bg-bg p-1.5">
          {chats.length === 0 ? (
            <div className="px-2 py-1 text-[11px] text-text-subtle">
              Нет зарегистрированных чатов. Добавь их на вкладке «Чаты».
            </div>
          ) : (
            chats.map((c) => (
              <label
                key={c.chatId}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs text-text hover:bg-bg2"
              >
                <input
                  type="checkbox"
                  checked={targetChatIds.includes(c.chatId)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...targetChatIds, c.chatId]
                      : targetChatIds.filter((id) => id !== c.chatId);
                    onChange({ ...config, targetChatIds: next });
                  }}
                />
                <span>{c.chatTitle ?? <span className="text-text-subtle">— без названия —</span>}</span>
                <span className="font-mono text-[10px] text-text-subtle">{c.chatId}</span>
              </label>
            ))
          )}
        </div>
      </div>
      <div>
        <label className={fieldLabelCls}>Шаблон сообщения</label>
        <TemplateTextarea
          value={template}
          onChange={(v) => onChange({ ...config, template: v })}
          placeholder="Например: ✅ Зачислено {amount} {ccy} → заявка {orderId}"
          minRows={3}
        />
        <TemplatePlaceholderChips
          placeholders={placeholders}
          live={live}
          onInsert={(token) =>
            insertIntoLastTextarea(token, (v) => onChange({ ...config, template: v }), template)
          }
        />
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_auto_auto]">
        <div>
          <label className={fieldLabelCls}>Формат</label>
          <div className="flex gap-1">
            {(['HTML', 'MarkdownV2', 'plain'] as const).map((m) => (
              <label
                key={m}
                className={`cursor-pointer rounded-md border px-2 py-1 text-[11px] ${
                  parseMode === m
                    ? 'border-[var(--accent-border)] bg-[var(--accent-bg)] text-accent'
                    : 'border-border bg-bg text-text2 hover:text-text'
                }`}
              >
                <input
                  type="radio"
                  className="sr-only"
                  checked={parseMode === m}
                  onChange={() => onChange({ ...config, parseMode: m })}
                />
                {m}
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className={fieldLabelCls}>Топик (id)</label>
          <input
            type="number"
            value={String(messageThreadId)}
            onChange={(e) =>
              onChange({
                ...config,
                messageThreadId: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            className={`${inputCls} h-8 w-24 text-xs`}
            placeholder="—"
          />
        </div>
        <label className="flex items-end gap-1.5 text-xs text-text">
          <input
            type="checkbox"
            checked={disableNotification}
            onChange={(e) => onChange({ ...config, disableNotification: e.target.checked })}
          />
          без звука
        </label>
        <label className="flex items-end gap-1.5 text-xs text-text">
          <input
            type="checkbox"
            checked={replyToOriginal}
            onChange={(e) => onChange({ ...config, replyToOriginal: e.target.checked })}
          />
          reply
        </label>
      </div>
    </div>
  );
}

function FormCustomWebhook({
  config,
  placeholders,
  live,
  onChange,
}: {
  config: Record<string, unknown>;
  placeholders: { name: string; type: VariableType }[];
  live: TemplateLiveValues | null;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const url = String(config.url ?? '');
  const method = String(config.method ?? 'POST') as 'POST' | 'PUT' | 'PATCH';
  const headers = (config.headers as Record<string, string> | undefined) ?? {};
  const bodyTemplate = String(config.bodyTemplate ?? '');
  const timeoutMs = Number(config.timeoutMs ?? 10000);
  const headerEntries = Object.entries(headers);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[auto_1fr_auto]">
        <div>
          <label className={fieldLabelCls}>Метод</label>
          <select
            value={method}
            onChange={(e) => onChange({ ...config, method: e.target.value })}
            className={`${selectCls} h-8 w-24 text-xs`}
          >
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
          </select>
        </div>
        <div>
          <label className={fieldLabelCls}>URL</label>
          <input
            value={url}
            onChange={(e) => onChange({ ...config, url: e.target.value })}
            className={`${inputCls} h-8 font-mono text-xs`}
            placeholder="https://api.example.com/hook"
          />
        </div>
        <div>
          <label className={fieldLabelCls}>Таймаут, мс</label>
          <input
            type="number"
            value={timeoutMs}
            onChange={(e) => onChange({ ...config, timeoutMs: Number(e.target.value) })}
            className={`${inputCls} h-8 w-24 text-xs`}
            min={1000}
            max={30000}
          />
        </div>
      </div>

      <div>
        <label className={fieldLabelCls}>Заголовки</label>
        <div className="space-y-1">
          {headerEntries.map(([k, v], i) => (
            <div key={i} className="flex items-center gap-1">
              <input
                value={k}
                onChange={(e) => {
                  const next = { ...headers };
                  delete next[k];
                  next[e.target.value] = v;
                  onChange({ ...config, headers: next });
                }}
                className={`${inputCls} h-7 flex-1 font-mono text-xs`}
                placeholder="key"
              />
              <input
                value={v}
                onChange={(e) => {
                  const next = { ...headers, [k]: e.target.value };
                  onChange({ ...config, headers: next });
                }}
                className={`${inputCls} h-7 flex-1 font-mono text-xs`}
                placeholder="value"
              />
              <button
                type="button"
                onClick={() => {
                  const next = { ...headers };
                  delete next[k];
                  onChange({ ...config, headers: next });
                }}
                className="text-xs text-danger hover:underline"
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              const newKey = `header-${headerEntries.length + 1}`;
              onChange({ ...config, headers: { ...headers, [newKey]: '' } });
            }}
            className="text-xs text-accent hover:underline"
          >
            + Заголовок
          </button>
        </div>
      </div>

      <div>
        <label className={fieldLabelCls}>Тело запроса (шаблон)</label>
        <TemplateTextarea
          value={bodyTemplate}
          onChange={(v) => onChange({ ...config, bodyTemplate: v })}
          placeholder='{"orderId":"{orderId}","amount":"{amount}","ccy":"{ccy}"}'
          minRows={4}
        />
        <TemplatePlaceholderChips
          placeholders={placeholders}
          live={live}
          onInsert={(token) =>
            insertIntoLastTextarea(token, (v) => onChange({ ...config, bodyTemplate: v }), bodyTemplate)
          }
        />
      </div>
    </div>
  );
}

/**
 * create-order: полноценная форма маппинга сообщения на заявку.
 * Каждое поле — «из сообщения» (named group) ИЛИ константа (MappingField).
 * exGive по умолчанию производится из «клиент получает» (переопределение
 * свёрнуто); operator при пустой настройке определяется по применившему
 * событие оператору; skipDuplicate защищает от повторной обработки edit'ов.
 */
function FormCreateOrder({
  config,
  placeholders,
  live,
  onChange,
}: {
  config: Record<string, unknown>;
  placeholders: { name: string; type: VariableType }[];
  live: TemplateLiveValues | null;
  onChange: (next: Record<string, unknown>) => void;
}) {
  // Валюты из справочника (вкл. добавленные через UI); fallback — встроенные.
  const { data: currenciesList } = useCurrencies();
  const ccyCodes = currenciesList?.length ? currenciesList.map((c) => c.code) : [...CCY_CODES];

  /** Атомарный patch config — несколько ключей одним onChange (без гонок closure). */
  function set(patch: Record<string, unknown>) {
    onChange({ ...config, ...patch });
  }
  const str = (k: string) => (config[k] as string | undefined) ?? '';

  /** Контрол константы-валюты: селект из справочника (как в других формах). */
  const ccyConst = (value: string, setV: (v: string | undefined) => void) => (
    <select
      value={value}
      onChange={(e) => setV(e.target.value || undefined)}
      className={`${selectCls} h-8 text-xs`}
    >
      <option value="">— валюта —</option>
      {ccyCodes.map((c) => (
        <option key={c} value={c}>
          {c}
        </option>
      ))}
    </select>
  );
  /** Контрол числовой константы (проценты/USDT). */
  const numConst =
    (ph: string) => (value: string, setV: (v: string | undefined) => void) => (
      <input
        value={value}
        onChange={(e) => setV(e.target.value || undefined)}
        className={`${inputCls} h-8 text-xs`}
        inputMode="decimal"
        placeholder={ph}
      />
    );

  // exGive-переопределение свёрнуто, пока не задано.
  const exGiveSet = !!(str('exGiveCcyGroup') || str('exGiveAmtGroup'));
  const [exGiveOpen, setExGiveOpen] = useState(exGiveSet);

  // Защита от дублей: default true (edit сообщения переобрабатывается повторно).
  const skipDuplicate = config.skipDuplicate === undefined ? true : Boolean(config.skipDuplicate);

  return (
    <div className="space-y-2.5">
      <div className="rounded-md border border-[var(--yellow-border)] bg-[var(--yellow-bg)] px-2 py-1.5 text-[11px] text-warning">
        create-order — сложное действие. Проверь маппинг кнопкой «⚡ Полная проверка» перед
        включением авто-применения.
      </div>

      <CfgSection title="Стороны">
        <ClientMappingField config={config} set={set} placeholders={placeholders} live={live} />
        <MappingField
          label="Исполнитель"
          cfg={config}
          set={set}
          live={live}
          groupKey="executorGroup"
          constKey="defaultExecutor"
          constPlaceholder="код счёта исполнителя"
          placeholders={placeholders}
          filterTypes={['anyText', 'tgUsername', 'name']}
          noneHint="Не задано — без исполнителя заявка не создастся."
        />
      </CfgSection>

      <CfgSection title="Клиент отдаёт">
        <MappingField
          label="Валюта"
          cfg={config}
          set={set}
          live={live}
          groupKey="clGiveCcyGroup"
          constKey="defaultClGiveCcy"
          constControl={ccyConst}
          placeholders={placeholders}
          filterTypes={['ccy', 'anyText']}
        />
        <MappingField
          label="Сумма"
          cfg={config}
          set={set}
          live={live}
          groupKey="clGiveAmtGroup"
          placeholders={placeholders}
          filterTypes={['amount', 'anyNumber']}
          hint="Сумма — БАЗА до коэффициента."
        />
      </CfgSection>

      <CfgSection title="Клиент получает">
        <MappingField
          label="Валюта"
          cfg={config}
          set={set}
          live={live}
          groupKey="clGetCcyGroup"
          constKey="defaultClGetCcy"
          constControl={ccyConst}
          placeholders={placeholders}
          filterTypes={['ccy', 'anyText']}
        />
        <MappingField
          label="Сумма"
          cfg={config}
          set={set}
          live={live}
          groupKey="clGetAmtGroup"
          placeholders={placeholders}
          filterTypes={['amount', 'anyNumber']}
        />
      </CfgSection>

      <CfgSection title="Исполнителю">
        <MappingField
          label="Валюта (исполнитель получает)"
          cfg={config}
          set={set}
          live={live}
          groupKey="exGetCcyGroup"
          constKey="defaultExGetCcy"
          constControl={ccyConst}
          placeholders={placeholders}
          filterTypes={['ccy', 'anyText']}
        />
        <MappingField
          label="Сумма (исполнитель получает)"
          cfg={config}
          set={set}
          live={live}
          groupKey="exGetAmtGroup"
          placeholders={placeholders}
          filterTypes={['amount', 'anyNumber']}
        />
        <div className="md:col-span-2">
          {!exGiveOpen ? (
            <button
              type="button"
              onClick={() => setExGiveOpen(true)}
              className="text-xs text-accent hover:underline"
            >
              + Переопределить «исполнитель отдаёт» (по умолчанию = клиент получает)
            </button>
          ) : (
            <div className="rounded-md border border-border-subtle bg-bg2 p-2">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-text-subtle">
                  Исполнитель отдаёт (переопределение)
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setExGiveOpen(false);
                    set({ exGiveCcyGroup: undefined, exGiveAmtGroup: undefined });
                  }}
                  className="text-[10px] text-danger hover:underline"
                >
                  ✕ сбросить
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <MappingField
                  label="Валюта"
                  cfg={config}
                  set={set}
                  live={live}
                  groupKey="exGiveCcyGroup"
                  placeholders={placeholders}
                  filterTypes={['ccy', 'anyText']}
                />
                <MappingField
                  label="Сумма"
                  cfg={config}
                  set={set}
                  live={live}
                  groupKey="exGiveAmtGroup"
                  placeholders={placeholders}
                  filterTypes={['amount', 'anyNumber']}
                />
              </div>
            </div>
          )}
        </div>
      </CfgSection>

      <CfgSection title="Дополнительно">
        <MappingField
          label="Коэффициент, %"
          cfg={config}
          set={set}
          live={live}
          groupKey="coefPctGroup"
          constKey="defaultCoefPct"
          constControl={numConst('например 1.5')}
          placeholders={placeholders}
          filterTypes={['amount', 'anyNumber']}
          hint="Не задавайте, если в сообщении сумма уже с наценкой."
        />
        <MappingField
          label="Фикс за платёжку, USDT"
          cfg={config}
          set={set}
          live={live}
          groupKey="fixUsdtGroup"
          constKey="defaultFixUsdt"
          constControl={numConst('например 3')}
          placeholders={placeholders}
          filterTypes={['amount', 'anyNumber']}
          hint="Транзитом: платит клиент 1:1, профит не меняет."
        />
        <MappingField
          label="Бонус исполнителю, USDT"
          cfg={config}
          set={set}
          live={live}
          groupKey="bonusUsdtGroup"
          constKey="defaultBonusUsdt"
          constControl={numConst('например 1')}
          placeholders={placeholders}
          filterTypes={['amount', 'anyNumber']}
          hint="Вычитается из профита компании."
        />
        <MappingField
          label="Курс клиента"
          cfg={config}
          set={set}
          live={live}
          groupKey="clRateGroup"
          placeholders={placeholders}
          filterTypes={['amount', 'anyNumber']}
          noneHint="Не задано — курс подставится из справочника."
        />
        <MappingField
          label="Курс исполнителя"
          cfg={config}
          set={set}
          live={live}
          groupKey="exRateGroup"
          placeholders={placeholders}
          filterTypes={['amount', 'anyNumber']}
          noneHint="Не задано — курс подставится из справочника."
        />
        <MappingField
          label="Дата заявки"
          cfg={config}
          set={set}
          live={live}
          groupKey="dateGroup"
          placeholders={placeholders}
          filterTypes={['date', 'anyText']}
          noneHint="Не задано — бизнес-дата = сегодня."
        />
      </CfgSection>

      <CfgSection title="Служебное">
        <MappingField
          label="Оператор"
          cfg={config}
          set={set}
          live={live}
          groupKey="operatorGroup"
          constKey="defaultOperator"
          constPlaceholder="username / email оператора"
          placeholders={placeholders}
          filterTypes={['tgUsername', 'email', 'name', 'anyText']}
          noneHint="Пусто — определится по оператору, применившему событие."
        />
        <label
          className="flex items-center gap-1.5 self-end pb-1 text-xs text-text"
          title="Telegram позволяет редактировать сообщения — правка переобрабатывается и может породить повторное событие. Защита не даст создать вторую заявку по тому же сообщению."
        >
          <input
            type="checkbox"
            checked={skipDuplicate}
            onChange={(e) => set({ skipDuplicate: e.target.checked })}
          />
          Защита от дублей
        </label>
      </CfgSection>
    </div>
  );
}

/** Секция формы create-order: заголовок + grid полей. */
function CfgSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border-subtle bg-bg p-2.5">
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-text-subtle">
        {title}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{children}</div>
    </div>
  );
}

type MappingMode = 'none' | 'group' | 'const';

/** Кнопка сегментного переключателя режима маппинга. */
function MappingModeBtn({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
        active ? 'bg-bg text-accent shadow-sm' : 'text-text-subtle hover:text-text'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Компактная переиспользуемая пара «из сообщения (named group) ИЛИ константа»
 * для одного поля маппинга create-order. Без constKey доступен только режим
 * «из сообщения». Патчит config атомарно через `set` (groupKey/constKey).
 */
function MappingField({
  label,
  cfg,
  set,
  groupKey,
  constKey,
  constControl,
  constPlaceholder,
  placeholders,
  filterTypes,
  hint,
  noneHint,
  live = null,
}: {
  label: string;
  cfg: Record<string, unknown>;
  set: (patch: Record<string, unknown>) => void;
  groupKey: string;
  constKey?: string;
  /** Кастомный контрол константы (селект валюты, число); default — текстовый input. */
  constControl?: (value: string, setV: (v: string | undefined) => void) => React.ReactNode;
  constPlaceholder?: string;
  placeholders: { name: string; type: VariableType }[];
  filterTypes?: VariableType[];
  hint?: string;
  noneHint?: string;
  /** Живые значения групп из sample — индикативно в option'ах селекта. */
  live?: TemplateLiveValues | null;
}) {
  const group = (cfg[groupKey] as string | undefined) ?? '';
  const constant = constKey ? ((cfg[constKey] as string | undefined) ?? '') : '';
  const hasConst = !!constKey;
  const [mode, setMode] = useState<MappingMode>(() =>
    group ? 'group' : hasConst && constant ? 'const' : 'none',
  );

  function switchMode(next: MappingMode) {
    setMode(next);
    const patch: Record<string, unknown> = {};
    if (next !== 'group') patch[groupKey] = undefined;
    if (constKey && next !== 'const') patch[constKey] = undefined;
    set(patch);
  }

  return (
    <div>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-1">
        <span className="text-[11px] font-medium text-text2">{label}</span>
        <div className="inline-flex gap-0.5 rounded-md bg-bg3 p-0.5">
          <MappingModeBtn active={mode === 'none'} onClick={() => switchMode('none')} title="Не задано">
            —
          </MappingModeBtn>
          <MappingModeBtn
            active={mode === 'group'}
            onClick={() => switchMode('group')}
            title="Взять из переменной сообщения"
          >
            из сообщения
          </MappingModeBtn>
          {hasConst && (
            <MappingModeBtn
              active={mode === 'const'}
              onClick={() => switchMode('const')}
              title="Фиксированное значение"
            >
              константа
            </MappingModeBtn>
          )}
        </div>
      </div>
      {mode === 'group' && (
        <GroupSelector
          value={group}
          onChange={(v) => set({ [groupKey]: v || undefined })}
          placeholders={placeholders}
          filterTypes={filterTypes}
          liveGroups={live?.groups}
        />
      )}
      {mode === 'const' &&
        hasConst &&
        (constControl ? (
          constControl(constant, (v) => set({ [constKey!]: v }))
        ) : (
          <input
            value={constant}
            onChange={(e) => set({ [constKey!]: e.target.value || undefined })}
            className={`${inputCls} h-8 text-xs`}
            placeholder={constPlaceholder}
          />
        ))}
      {mode === 'none' && noneHint && (
        <div className="text-[10px] text-text-subtle">{noneHint}</div>
      )}
      {hint && <div className="mt-1 text-[10px] text-text-subtle">{hint}</div>}
    </div>
  );
}

/**
 * Поле «Клиент» — особый случай с тремя источниками: чат-источник сообщения
 * (clientFromChat), named group (clientGroup) или константа (defaultClient).
 */
function ClientMappingField({
  config,
  set,
  placeholders,
  live = null,
}: {
  config: Record<string, unknown>;
  set: (patch: Record<string, unknown>) => void;
  placeholders: { name: string; type: VariableType }[];
  /** Живые значения групп из sample — индикативно в option'ах селекта. */
  live?: TemplateLiveValues | null;
}) {
  const fromChat = Boolean(config.clientFromChat);
  const group = (config.clientGroup as string | undefined) ?? '';
  const constant = (config.defaultClient as string | undefined) ?? '';
  const [mode, setMode] = useState<'none' | 'chat' | 'group' | 'const'>(() =>
    fromChat ? 'chat' : group ? 'group' : constant ? 'const' : 'none',
  );

  function switchMode(next: 'none' | 'chat' | 'group' | 'const') {
    setMode(next);
    set({
      clientFromChat: next === 'chat' ? true : undefined,
      clientGroup: next === 'group' ? group || undefined : undefined,
      defaultClient: next === 'const' ? constant || undefined : undefined,
    });
  }

  return (
    <div>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-1">
        <span className="text-[11px] font-medium text-text2">Клиент</span>
        <div className="inline-flex gap-0.5 rounded-md bg-bg3 p-0.5">
          <MappingModeBtn active={mode === 'none'} onClick={() => switchMode('none')} title="Не задано">
            —
          </MappingModeBtn>
          <MappingModeBtn
            active={mode === 'chat'}
            onClick={() => switchMode('chat')}
            title="Клиент = счёт, привязанный к чату-источнику сообщения"
          >
            из чата
          </MappingModeBtn>
          <MappingModeBtn
            active={mode === 'group'}
            onClick={() => switchMode('group')}
            title="Взять из переменной сообщения"
          >
            из сообщения
          </MappingModeBtn>
          <MappingModeBtn
            active={mode === 'const'}
            onClick={() => switchMode('const')}
            title="Фиксированный код счёта"
          >
            константа
          </MappingModeBtn>
        </div>
      </div>
      {mode === 'chat' && (
        <div className="text-[10px] text-text-subtle">
          Клиент определится по чату-источнику сообщения.
        </div>
      )}
      {mode === 'group' && (
        <GroupSelector
          value={group}
          onChange={(v) => set({ clientGroup: v || undefined })}
          placeholders={placeholders}
          filterTypes={['anyText', 'tgUsername', 'name']}
          liveGroups={live?.groups}
        />
      )}
      {mode === 'const' && (
        <input
          value={constant}
          onChange={(e) => set({ defaultClient: e.target.value || undefined })}
          className={`${inputCls} h-8 text-xs`}
          placeholder="код счёта клиента"
        />
      )}
      {mode === 'none' && (
        <div className="text-[10px] text-text-subtle">
          Не задано — без клиента заявка не создастся.
        </div>
      )}
    </div>
  );
}

/**
 * update-chat-balance: парсинг мультивалютных балансов из сообщения чата.
 * Из named groups берётся код валюты и сумма; баланс пишется чату-источнику
 * либо явно выбранному чату. ccyAliases — маппинг «токен в сообщении → код
 * валюты» для символов/слов («₽» → RUB, «₮» → USDT).
 *
 * Если сообщение размечено целиком (одно совпадение), нумерованные продолжения
 * ccy2/amount2, ccy3/amount3… handler подхватывает автоматически — форма лишь
 * подсказывает это и (по live-данным sample) показывает число найденных пар.
 */
function FormUpdateChatBalance({
  config,
  placeholders,
  chats,
  live,
  onChange,
}: {
  config: Record<string, unknown>;
  placeholders: { name: string; type: VariableType }[];
  chats: TelegramListeningChat[];
  live: TemplateLiveValues | null;
  onChange: (next: Record<string, unknown>) => void;
}) {
  // Валюты из справочника (вкл. добавленные через UI); fallback — встроенные.
  const { data: currenciesList } = useCurrencies();
  const ccyCodes = currenciesList?.length ? currenciesList.map((c) => c.code) : [...CCY_CODES];

  // В отличие от update-rate, Zod-контракт не имеет defaults (min(1) required) —
  // пустое значение показываем как «— выбери переменную —», не фабрикуем 'ccy'.
  const ccyGroup = String(config.ccyGroup ?? '');
  const amountGroup = String(config.amountGroup ?? '');
  const zeroAmount = config.zeroAmount === true;
  const targetChatId = (config.targetChatId as string | undefined) ?? '';
  const ccyAliases = (config.ccyAliases as Record<string, string> | undefined) ?? {};
  const aliasEntries = Object.entries(ccyAliases);
  // Редактор алиасов по умолчанию свёрнут — раскрыт только если пары уже есть.
  const [aliasesOpen, setAliasesOpen] = useState(aliasEntries.length > 0);

  const chatOptions = [
    { value: '', label: '— исходный чат —', hint: 'баланс пишется чату-источнику сообщения' },
    ...chats.map((c) => ({
      value: c.chatId,
      label: c.chatTitle ?? c.chatId,
      hint: c.chatId,
    })),
  ];

  // Число пар, обнаруженных в live-значениях sample: базовая + нумерованные
  // продолжения (та же логика «до первой дыры», что в handler-е на бэке).
  const livePairCount = useMemo(() => {
    const groups = live?.groups;
    if (!groups || !ccyGroup || !amountGroup) return null;
    if (!groups[ccyGroup] || !groups[amountGroup]) return null;
    let n = 1;
    for (let i = 2; i <= 50; i++) {
      if (!groups[`${ccyGroup}${i}`] || !groups[`${amountGroup}${i}`]) break;
      n++;
    }
    return n;
  }, [live, ccyGroup, amountGroup]);

  /** Пустой Record не храним в config — Zod-контракт: ccyAliases опционален. */
  function setAliases(next: Record<string, string>) {
    onChange({ ...config, ccyAliases: Object.keys(next).length > 0 ? next : undefined });
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label className={fieldLabelCls}>Группа валюты</label>
          <GroupSelector
            value={ccyGroup}
            onChange={(v) => onChange({ ...config, ccyGroup: v })}
            placeholders={placeholders}
            filterTypes={['ccy', 'anyText']}
            liveGroups={live?.groups}
          />
          <div className="mt-1.5 text-[10px] text-text-subtle">
            Откуда взять код валюты (RUB / USDT; символы — через алиасы ниже).
          </div>
        </div>
        <div>
          <label className={fieldLabelCls}>Группа суммы</label>
          {zeroAmount ? (
            <div className="flex h-9 items-center text-xs text-text2">не нужна — валюта обнуляется</div>
          ) : (
            <GroupSelector
              value={amountGroup}
              onChange={(v) => onChange({ ...config, amountGroup: v })}
              placeholders={placeholders}
              filterTypes={['amount', 'anyNumber']}
              liveGroups={live?.groups}
            />
          )}
          <div className="mt-1.5 text-[10px] text-text-subtle">
            Откуда взять сумму баланса из сообщения.
          </div>
        </div>
      </div>

      <label className="flex items-start gap-2 text-xs text-text">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={zeroAmount}
          onChange={(e) => onChange({ ...config, zeroAmount: e.target.checked || undefined })}
        />
        <span>
          Обнулить валюту (сумма не из сообщения)
          <span className="block text-[10px] text-text-subtle">
            Для команд удаления строки баланса, например «/b del rub»: бот отвечает «Empty», валюты в ответе нет —
            без такого правила удалённая строка висит в панели.
          </span>
        </span>
      </label>

      <div className="text-[10px] text-text-subtle">
        Нумерованные продолжения (ccy2/amount2, ccy3/amount3…) подхватываются автоматически — все
        валюты сообщения обновятся одним событием.
        {livePairCount != null && livePairCount > 1 && (
          <span className="ml-1 font-medium text-accent">Найдено пар: {livePairCount}.</span>
        )}
      </div>

      <div>
        <label className={fieldLabelCls}>Чат, которому записать баланс</label>
        <Combobox
          value={targetChatId}
          onChange={(v) => onChange({ ...config, targetChatId: v || undefined })}
          options={chatOptions}
          placeholder="— исходный чат —"
        />
        <div className="mt-1.5 text-[10px] text-text-subtle">
          По умолчанию баланс пишется чату, из которого пришло сообщение.
        </div>
      </div>

      {/* ccyAliases — опциональный маппинг токенов сообщения на коды валют */}
      <div>
        {!aliasesOpen ? (
          <button
            type="button"
            onClick={() => setAliasesOpen(true)}
            className="text-xs text-accent hover:underline"
          >
            + Алиасы валют (₽ → RUB, ₮ → USDT …)
          </button>
        ) : (
          <div>
            <label className={fieldLabelCls}>Алиасы валют (токен в сообщении → валюта)</label>
            <div className="space-y-1">
              {aliasEntries.map(([token, ccy], i) => (
                <div key={i} className="flex items-center gap-1">
                  <input
                    value={token}
                    onChange={(e) => {
                      // Переименование ключа с сохранением порядка пар.
                      const next: Record<string, string> = {};
                      for (const [k, v] of aliasEntries) next[k === token ? e.target.value : k] = v;
                      setAliases(next);
                    }}
                    className={`${inputCls} h-7 w-32 font-mono text-xs`}
                    placeholder="₽ / руб"
                  />
                  <span className="text-xs text-text-subtle">→</span>
                  <select
                    value={ccy}
                    onChange={(e) => setAliases({ ...ccyAliases, [token]: e.target.value })}
                    className={`${selectCls} h-7 w-32 text-xs`}
                  >
                    {ccyCodes.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      const next = { ...ccyAliases };
                      delete next[token];
                      setAliases(next);
                    }}
                    className="text-xs text-danger hover:underline"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  // Пустой ключ уже занят пустой парой — не затираем её.
                  const newKey = ccyAliases[''] === undefined ? '' : `токен-${aliasEntries.length + 1}`;
                  setAliases({ ...ccyAliases, [newKey]: ccyCodes[0] ?? 'USDT' });
                }}
                className="text-xs text-accent hover:underline"
              >
                + Пара
              </button>
            </div>
            <div className="mt-1 text-[10px] text-text-subtle">
              Если валюта в сообщении обозначена символом или словом («₽», «руб») — сопоставь её
              коду валюты системы.
            </div>
          </div>
        )}
      </div>

      <div className="rounded-md border border-[var(--accent-border)] bg-[var(--accent-bg)] px-2 py-1.5 text-[11px] text-accent">
        Совет: разметьте сообщение целиком (нумерованные пары → одно событие на все валюты) либо
        включите «Все совпадения» в правиле — тогда будет событие на каждую валютную строку.
      </div>
    </div>
  );
}

